import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { NotificationTemplate } from '@/types/notificationTemplate.types';

interface TemplateTableProps {
  templates: NotificationTemplate[];
  onEdit: (template: NotificationTemplate) => void;
}

const EVENT_TYPE_ORDER = ['auth', 'document', 'booking'];

function formatTemplateName(name: string): string {
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function TemplateTable({ templates, onEdit }: TemplateTableProps) {
  const { t } = useTranslation();

  const grouped = EVENT_TYPE_ORDER.map((eventType) => ({
    eventType,
    label: t(`notificationTemplates.filters.${eventType}`),
    items: templates.filter((tpl) => tpl.eventType === eventType),
  })).filter((group) => group.items.length > 0);

  return (
    <div className='space-y-6'>
      {grouped.map((group) => (
        <div key={group.eventType} className='overflow-hidden rounded-lg bg-white shadow'>
          <div className='border-b border-gray-200 bg-gray-50 px-6 py-3'>
            <h3 className='text-sm font-semibold text-gray-700'>{group.label}</h3>
          </div>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.name')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.channel')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.subject')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.status')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.updated')}
                </th>
                <th className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('notificationTemplates.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white'>
              {group.items.map((tpl) => (
                <tr key={tpl.templateId} className='hover:bg-gray-50'>
                  <td className='whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900'>
                    {formatTemplateName(tpl.name)}
                  </td>
                  <td className='whitespace-nowrap px-6 py-4'>
                    <span className='inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800'>
                      {tpl.channel}
                    </span>
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-500 truncate max-w-xs'>{tpl.subject ?? '—'}</td>
                  <td className='whitespace-nowrap px-6 py-4'>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                        tpl.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600',
                      )}>
                      {tpl.isActive
                        ? t('notificationTemplates.status.active')
                        : t('notificationTemplates.status.inactive')}
                    </span>
                  </td>
                  <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                    {new Date(tpl.updatedAt).toLocaleDateString()}
                  </td>
                  <td className='whitespace-nowrap px-6 py-4 text-right'>
                    <button
                      onClick={() => onEdit(tpl)}
                      className='inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50'>
                      <Pencil className='h-3.5 w-3.5' />
                      {t('notificationTemplates.table.edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
